
import Vue from 'vue';
import { generateUniqueId } from '@/utils';
import { deprecationMixin } from './../mixins/deprecationMixin';

/**
 * A display input field functions to guide a user as they input alphanumeric content during an experience.
 */
export default Vue.extend({
    name: 'IrisDisplayInputFieldUnspecified',
    inheritAttrs: false,
    model: {
        event: 'display-input-field-input',
    },
    mixins: [deprecationMixin],
    props: {
        /**
         * Sets the id (HTML global attribute) for the component. If an id is not provided, one will be generated automatically.
         */
        elementId: String,
        /**
         * Sets the actionable text
         */
        actiontext: {
            type: String,
            default: '',
        },
        /**
         * Sets the alignment of the input display, action text, error text, and helper text
         */
        align: {
            type: String,
            default: 'center',
            validator: (value: string) => {
                return ['left', 'center', 'right'].includes(value.toLowerCase());
            },
        },
        /**
         * Sets the error state.<br /><br />
         * It is suggested to set hasError to 'false' when event 'focus:displayInputFieldUnspecified' is emitted.
         */
        hasError: {
            type: Boolean,
            default: false,
        },
        /**
         * Sets the error message
         */
        errortext: {
            type: String,
            default: '',
        },
        /**
         * Sets the helper text
         */
        helpertext: {
            type: String,
            default: '',
        },
        /**
         * (Deprecated: Use elementId instead!) The textfield id.
         */
        id: String,
        /**
         * Marks the input as either an optional or required field
         */
        isOptional: {
            type: Boolean,
            default: false,
        },
        /**
         * Sets the popup keyboard type on supported mobile devices
         */
        keyboardType: {
            type: String,
            default: 'text',
            validator: (value: string) => {
                return ['text', 'decimal', 'numeric', 'tel', 'search', 'email', 'url'].includes(value.toLowerCase());
            },
        },
        /**
         * Sets the minimum length of the input
         */
        minlength: {
            type: Number,
            default: 1,
            validator: (value: number) => {
                return value > 0 && value < 9;
            },
        },
        /**
         * Sets the maximum length of the input
         */
        maxlength: {
            type: Number,
            default: 8,
            validator: (value: number) => {
                return value > 0 && value < 9;
            },
        },
        /**
         * The input value
         */
        value: String,
        /**
         * An object containing the strings used in the select dropdown.<br /><br />
         * <strong>Keys are:</strong><br /><br />
         * errortext: Sets the text that displays when hasError is true.<br /><br />
         * helpertext: Sets the helperText for the input.<br /><br />
         * actiontext: Sets the actiontext that appears in the field.<br /><br />
         */
        strings: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Sets the spellcheck global attribute for the HTML input.
         */
        allowBrowserSpellCheck: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            identifier_: this.elementId || this.id || generateUniqueId('irisv_display_input_field_unspecified') as string,
            internalError_: this.hasError,
        };
    },
    computed: {
        local_value: {
            get(): any {
                return this.value;
            },
            set() {
                // purposely left empty
            },
        },
        mergedStrings(): any {
            return {
                errortext: this.errortext,
                helpertext: this.helpertext,
                actiontext: this.actiontext,
                ...this.strings,
            };
        },
        actionTextIdentifier_(): string {
            return `action_text-${this.identifier_}`;
        },
        errorTextIdentifier_(): string {
            return `error_text-${this.identifier_}`;
        },
        helperTextIdentifier_(): string {
            return `helper_text-${this.identifier_}`;
        },
        showActionText(): boolean {
            return this.value && this.value.length > 0 ? false : true;
        },
    },
    methods: {
        onBlur(event: Event) {
            /**
             * Emitted when the input blurs.
             */
            this.$emit('display-input-field-blur', event);
        },
        onFocus(event: Event) {
            /**
             * Emitted when the input gets focus.
             */
            this.$emit('display-input-field-focus', event);
        },
        onInput(event: Event) {
            /**
             * Emitted when the input changes.
             */
            this.$emit('display-input-field-input', (event.target as HTMLInputElement).value);
        },
        setDisplaySizes() {
            const label = this.$refs.label as HTMLElement;
            if (!label) { return; }

            const input = this.$refs.input as HTMLInputElement;
            if (!input) { return; }

            const actionTextContainer = this.$refs.actionTextContainer as HTMLElement;
            if (!actionTextContainer) { return; }

            // Use actual text length or minimum of 1 for calculation
            const actualTextLength = this.value ? this.value.length : 1;
            const maxAllowedFontSize = 60;
            const buffer = 1.7; // This number works best for adding a buffer in the possibleFontSize calculation. I tried others, but they would result in incorrect sizes.

            // Calculate a possible font size based on actual text length
            const possibleFontSize = Math.floor(label.offsetWidth / actualTextLength * buffer);
            const fontSizeNumber = Math.max(16, Math.min(possibleFontSize, maxAllowedFontSize)); // find the size to use between 16 and 60
            const actualFontSize = fontSizeNumber + 'px';
            input.style.fontSize = actualFontSize;

            // Dynamically set the width based on the text content
            if (this.value && this.value.length > 0) {
                // Create a temporary element to measure text width
                const tempSpan = document.createElement('span');
                tempSpan.style.fontSize = actualFontSize;
                tempSpan.style.fontFamily = getComputedStyle(input).fontFamily;
                tempSpan.style.visibility = 'hidden';
                tempSpan.style.position = 'absolute';
                tempSpan.style.whiteSpace = 'nowrap';
                tempSpan.textContent = this.value;
                document.body.appendChild(tempSpan);

                const textWidth = tempSpan.offsetWidth;
                document.body.removeChild(tempSpan);

                // Set input width to match text width
                input.style.width = Math.max(60, textWidth) + 'px';
            } else {
                // Reset to minimum width when no text
                input.style.width = actualFontSize;
            }

            // Position the action text
            actionTextContainer.style.top = fontSizeNumber - 26 + 'px';
        },
    },
    watch: {
        hasError(newVal) {
            this.internalError_ = newVal;
        },
        value() {
            // Update display sizes when value changes
            this.$nextTick(() => {
                this.setDisplaySizes();
            });
        },
    },
    mounted() {
        this.setDisplaySizes();
        window.addEventListener('resize', this.setDisplaySizes);
    },
    updated() {
        this.setDisplaySizes();
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.setDisplaySizes);
    },
});
